import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { CreateContactDialog } from "@/components/contacts/CreateContactDialog";
import type { Contact } from "@/hooks/useContacts";
import { VENDOR_TAG, isTaggedVendor, matchVendors, useTagAsVendor } from "@/hooks/useVendorContacts";

interface VendorSearchInputProps {
  vendors: Contact[];           // from useVendorContacts() -- all organizations
  onSelect: (vendor: Contact) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Search box + dropdown for picking a vendor. Empty search lists tagged
 * vendors; typing searches every organization contact. Picking an untagged
 * one auto-tags it as a vendor. Once the user has typed, the list ends with an
 * option to create a new organization contact (pre-filled and tagged as a
 * vendor), unless a contact with that exact name already exists.
 */
export function VendorSearchInput({ vendors, onSelect, placeholder = "Search vendors...", className }: VendorSearchInputProps) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const tagAsVendor = useTagAsVendor();

  const matches = matchVendors(vendors, search);
  const term = search.trim();
  const searching = term.length > 0;
  const exactMatch = matches.some((v) => (v.company ?? "").trim().toLowerCase() === term.toLowerCase());

  const pick = (vendor: Contact) => {
    if (!isTaggedVendor(vendor)) tagAsVendor.mutate(vendor);
    onSelect(vendor);
    setSearch("");
    setOpen(false);
  };

  const startCreate = () => {
    setCreateName(term);
    setOpen(false);
    setCreateOpen(true);
  };

  const handleCreated = (vendor: Contact) => {
    onSelect(vendor);
    setSearch("");
  };

  return (
    <div className="relative">
      <Input
        placeholder={placeholder}
        value={search}
        onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={className}
      />
      {open && (
        <div className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto rounded-md border bg-popover shadow-md">
          {matches.map((v) => (
            <button
              key={v.id}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
              onMouseDown={() => pick(v)}
            >
              {v.company}
            </button>
          ))}
          {searching ? (
            !exactMatch && (
              <button
                type="button"
                className={cn(
                  "w-full flex items-center gap-1.5 px-3 py-2 text-sm text-left text-primary hover:bg-accent",
                  matches.length > 0 && "border-t",
                )}
                onMouseDown={startCreate}
              >
                <Plus className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">Create new contact "{term}"</span>
              </button>
            )
          ) : (
            <p className={cn("px-3 py-2 text-xs text-muted-foreground", matches.length > 0 && "border-t")}>
              Type to search all contacts
            </p>
          )}
        </div>
      )}
      <CreateContactDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaults={{ type: "organization", company: createName, tags: [VENDOR_TAG] }}
        onCreated={handleCreated}
      />
    </div>
  );
}
